import React, { createContext, useContext, useState, useCallback } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';

interface ConfirmationOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'primary' | 'secondary';
  icon?: string;
}

interface AlertOptions {
  title?: string;
  message: string;
  variant?: 'success' | 'danger' | 'warning' | 'info';
  icon?: string;
  duration?: number;
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
  alert: (options: AlertOptions) => void;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within ConfirmationProvider');
  }
  return context;
};

interface ConfirmationState extends ConfirmationOptions {
  show: boolean;
  resolve?: (value: boolean) => void;
}

interface AlertState extends AlertOptions {
  show: boolean;
}

export const ConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(null);
  const [alert, setAlert] = useState<AlertState | null>(null);

  const confirm = useCallback((options: ConfirmationOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmation({
        ...options,
        show: true,
        resolve,
      });
    });
  }, []);

  const showAlert = useCallback((options: AlertOptions) => {
    setAlert({
      ...options,
      show: true,
    });
    
    // Auto-hide after duration (default 5 seconds)
    setTimeout(() => {
      setAlert(null);
    }, (options.duration || 5000));
  }, []);

  const handleConfirm = () => {
    if (confirmation?.resolve) {
      confirmation.resolve(true);
    }
    setConfirmation(null);
  };

  const handleCancel = () => {
    if (confirmation?.resolve) {
      confirmation.resolve(false);
    }
    setConfirmation(null);
  };

  const handleAlertClose = () => {
    setAlert(null);
  };

  const getAlertIcon = (variant?: string) => {
    switch (variant) {
      case 'success':
        return 'fa-check-circle';
      case 'danger':
        return 'fa-exclamation-circle';
      case 'warning':
        return 'fa-exclamation-triangle';
      case 'info':
        return 'fa-info-circle';
      default:
        return 'fa-info-circle';
    }
  };

  return (
    <ConfirmationContext.Provider value={{ confirm, alert: showAlert }}>
      {children}
      <ToastContainer
        position="top-center"
        className="confirmation-toast-container"
        style={{ zIndex: 9999 }}
      >
        {confirmation && (
          <Toast
            show={confirmation.show}
            autohide={false}
            className="confirmation-toast"
          >
            <Toast.Header className={`bg-${confirmation.variant || 'warning'} text-white`}>
              {confirmation.icon && (
                <i className={`fas ${confirmation.icon} me-2`}></i>
              )}
              <strong className="me-auto">
                {confirmation.title || 'Confirm Action'}
              </strong>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleCancel}
                aria-label="Close"
              ></button>
            </Toast.Header>
            <Toast.Body>
              <p className="mb-3">{confirmation.message}</p>
              <div className="d-flex gap-2 justify-content-end">
                <button
                  className={`btn btn-sm btn-outline-${confirmation.variant || 'warning'}`}
                  onClick={handleCancel}
                >
                  {confirmation.cancelText || 'Cancel'}
                </button>
                <button
                  className={`btn btn-sm btn-${confirmation.variant || 'warning'}`}
                  onClick={handleConfirm}
                >
                  {confirmation.confirmText || 'Confirm'}
                </button>
              </div>
            </Toast.Body>
          </Toast>
        )}
        
        {alert && (
          <Toast
            show={alert.show}
            autohide
            delay={alert.duration || 5000}
            onClose={handleAlertClose}
            className="alert-toast"
          >
            <Toast.Header className={`bg-${alert.variant || 'info'} text-white`}>
              <i className={`fas ${alert.icon || getAlertIcon(alert.variant)} me-2`}></i>
              <strong className="me-auto">
                {alert.title || 'Notification'}
              </strong>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleAlertClose}
                aria-label="Close"
              ></button>
            </Toast.Header>
            <Toast.Body>
              <p className="mb-0">{alert.message}</p>
            </Toast.Body>
          </Toast>
        )}
      </ToastContainer>
    </ConfirmationContext.Provider>
  );
};

